import React from "react";
import * as hrmsService from "../../../services/hrmsService";
import { notifyHrAccessChanged } from "../../../hooks/useHrAccess";
import { FormFields, cleanValues } from "../hrForm";
import { LoadState, PrimaryButton } from "../hrComponents";
import { cardStyle, useLoad } from "../hrUi";
import { toast } from "react-toastify";

const COUNTRIES = [
  { label: "United Arab Emirates (AED)", value: "AE" },
  { label: "India (INR)", value: "IN" },
];
// Whole/minor unit names by country, so the example matches the currency the tenant actually chose - "rupee" means
// nothing to a UAE company. Shown with no example at all until a country is picked.
const MONEY_UNITS = { AE: ["dirham", "fils"], IN: ["rupee", "paise"] };
const roundingOptions = (country) => {
  const units = MONEY_UNITS[country];
  return [
    { label: units ? `Whole unit (${units[0]})` : "Whole unit", value: "WHOLE_UNIT" },
    { label: units ? `Minor unit (${units[1]})` : "Minor unit", value: "MINOR_UNIT" },
  ];
};
const FIELDS = (locked, country) => [
  { key: "country_pack", label: "Country", type: "select", required: true, options: COUNTRIES, disabled: locked, hint: locked ? "The country cannot be changed once it is set." : "Sets the currency, statutory rules and default holidays." },
  { key: "timezone", label: "Timezone", type: "select", options: [{ label: "Asia/Dubai", value: "Asia/Dubai" }, { label: "Asia/Kolkata", value: "Asia/Kolkata" }] },
  { key: "employee_code_prefix", label: "Employee code prefix", type: "text", max: 10, hint: "New employees get EMP0001, EMP0002 … with this prefix." },
  { key: "days_basis", label: "Salary days basis", type: "select", options: [{ label: "Calendar days", value: "CALENDAR" }, { label: "Fixed 30 days", value: "FIXED_30" }, { label: "Working days", value: "WORKING" }] },
  { key: "rounding_mode", label: "Rounding", type: "select", options: roundingOptions(country) },
];

// The first save switches HR on for the company: it chooses the country pack and seeds defaults.
export default function GeneralTab() {
  const { data, loading, error, reload } = useLoad(() => hrmsService.getSettings(), []);
  const [values, setValues] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  React.useEffect(() => {
    if (data) setValues({ country_pack: data.country_pack, timezone: data.timezone, employee_code_prefix: data.employee_code_prefix, days_basis: data.days_basis, rounding_mode: data.rounding_mode });
  }, [data]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await hrmsService.saveSettings(cleanValues(values));
      if (res?.success) {
        toast.success(res.message || "Settings saved");
        notifyHrAccessChanged();
        reload();
      } else toast.error(hrmsService.responseMessage(res));
    } finally {
      setSaving(false);
    }
  };

  return (
    <LoadState loading={loading} error={error} onRetry={reload}>
      <div style={{ ...cardStyle, padding: 24, maxWidth: 560 }}>
        {data && !data.configured ? <div style={{ background: "#FEF3C7", color: "#92400E", padding: 12, borderRadius: 8, fontSize: 13, marginBottom: 16 }}>Choose your country to finish setting up HR. Nothing else can be added until you do.</div> : null}
        <FormFields values={values} onChange={(k, v) => setValues((s) => ({ ...s, [k]: v }))} fields={FIELDS(!!data?.configured, values.country_pack)} />
        <PrimaryButton onClick={save} disabled={saving || !values.country_pack}>
          Save settings
        </PrimaryButton>
      </div>
    </LoadState>
  );
}
